import React from 'react';
import { Box, Button, Flex, Icon, Image, Input, SimpleGrid, Text, Textarea } from '@chakra-ui/react';
import { FiSearch } from 'react-icons/fi';
import { ff } from '../../../theme';
import BountyBrandSearchInput from './BountyBrandSearchInput';
import { PROPOSAL_NOTE_LIMIT, PROPOSAL_NOTE_WARN_AT } from '../constants';
import {
  CATEGORY_OPTIONS, PROPOSAL_FIELDS, ProposalFieldKey, ProposalFormValues,
} from '../proposalFormUtils';
import { CATEGORY_ICONS } from '../categoryIcons';
import { bountyColors, bountyV2Colors } from '../theme';

type Props = {
  values: ProposalFormValues;
  errors: Partial<Record<ProposalFieldKey, string>>;
  onChange: (key: ProposalFieldKey, value: string) => void;
};

const fieldId = (key: ProposalFieldKey) => `proposal-${key}`;

/** Board field shell: 48px, 14px radius, hairline border; red when invalid. */
const shellStyle = (invalid: boolean) => ({
  minH: '48px',
  px: '14px',
  borderRadius: '14px',
  border: '1px solid',
  borderColor: invalid ? bountyV2Colors.errorText : ff.border,
  bg: 'white',
  _focusWithin: { borderColor: invalid ? bountyV2Colors.errorText : ff.ink, boxShadow: `0 0 0 1px ${invalid ? bountyV2Colors.errorText : ff.ink}` },
});

const inputStyle = {
  variant: 'unstyled' as const,
  flex: 1,
  minW: 0,
  h: '46px',
  fontSize: '16px',
  color: ff.ink,
  _placeholder: { color: ff.muted },
};

const ProposalFormFields: React.FC<Props> = ({ values, errors, onChange }) => {
  const fieldLabel = (key: ProposalFieldKey, extra?: React.ReactNode) => (
    <Flex justify="space-between" align="baseline" mb="6px">
      <Text as="label" htmlFor={fieldId(key)} fontSize="14px" fontWeight={600} color={ff.ink}>
        {PROPOSAL_FIELDS[key].label}
        <Text as="span" color={bountyV2Colors.required} ml="3px">*</Text>
      </Text>
      {extra}
    </Flex>
  );

  const fieldHelp = (key: ProposalFieldKey) => {
    const message = errors[key] ?? PROPOSAL_FIELDS[key].helper;
    if (!message) return null;
    return (
      <Text fontSize="13px" mt="6px" fontWeight={errors[key] ? 700 : 400} color={errors[key] ? bountyV2Colors.errorText : ff.text2}>
        {message}
      </Text>
    );
  };

  const noteLength = values.proposalNote.length;

  return (
    <Flex direction="column" gap="18px">
      <Box>
        {fieldLabel('companyName')}
        <Flex align="center" gap="10px" color={ff.text2} {...shellStyle(!!errors.companyName)}>
          <Icon as={FiSearch} boxSize="18px" flex="none" />
          <BountyBrandSearchInput
            value={values.companyName}
            onChange={value => onChange('companyName', value)}
            placeholder="Start typing to search brands"
            inputStyle={{ ...inputStyle, id: fieldId('companyName') }}
          />
        </Flex>
        {fieldHelp('companyName')}
      </Box>

      <Box>
        <Text id={fieldId('category')} fontSize="14px" fontWeight={600} color={ff.ink} mb="8px">
          {PROPOSAL_FIELDS.category.label}
          <Text as="span" color={bountyV2Colors.required} ml="3px">*</Text>
        </Text>
        <Flex role="radiogroup" aria-labelledby={fieldId('category')} wrap="wrap" gap="8px">
          {CATEGORY_OPTIONS.map(opt => {
            const on = values.category === opt.value;
            return (
              <Button
                key={opt.value}
                role="radio"
                aria-checked={on}
                h="44px"
                pl="10px"
                pr="14px"
                borderRadius="full"
                fontSize="14px"
                fontWeight={600}
                bg={on ? ff.lime : 'white'}
                color={on ? ff.ink : ff.body}
                border={on ? '1.5px solid' : '1px solid'}
                borderColor={on ? ff.ink : errors.category ? bountyV2Colors.errorText : ff.border}
                _hover={{ bg: on ? ff.lime : ff.grey }}
                _active={{ bg: on ? ff.lime : ff.grey }}
                onClick={() => onChange('category', opt.value)}
              >
                <Image src={CATEGORY_ICONS[opt.value]} alt="" w="20px" h="20px" objectFit="contain" flex="none" mr="6px" />
                {opt.label}
              </Button>
            );
          })}
        </Flex>
        {fieldHelp('category')}
      </Box>

      <SimpleGrid columns={2} spacing="10px">
        <Box minW={0}>
          {fieldLabel('website')}
          <Flex align="center" {...shellStyle(!!errors.website)}>
            <Input
              {...inputStyle}
              id={fieldId('website')}
              type="url"
              inputMode="url"
              placeholder="https://example.com"
              value={values.website}
              onChange={e => onChange('website', e.target.value)}
            />
          </Flex>
          {fieldHelp('website')}
        </Box>
        <Box minW={0}>
          {fieldLabel('instagramHandle')}
          <Flex align="center" {...shellStyle(!!errors.instagramHandle)}>
            <Input
              {...inputStyle}
              id={fieldId('instagramHandle')}
              placeholder="@username"
              value={values.instagramHandle}
              onChange={e => onChange('instagramHandle', e.target.value)}
            />
          </Flex>
          {fieldHelp('instagramHandle')}
        </Box>
      </SimpleGrid>

      <Box>
        {fieldLabel('proposalNote', (
          <Text
            fontSize="13px"
            fontWeight={600}
            sx={{ fontVariantNumeric: 'tabular-nums' }}
            color={noteLength >= PROPOSAL_NOTE_WARN_AT ? bountyColors.amber : ff.text2}
          >
            {noteLength} / {PROPOSAL_NOTE_LIMIT}
          </Text>
        ))}
        <Box {...shellStyle(!!errors.proposalNote)} px={0}>
          <Textarea
            variant="unstyled"
            id={fieldId('proposalNote')}
            w="full"
            h="120px"
            px="14px"
            py="12px"
            resize="none"
            fontSize="16px"
            lineHeight="1.4"
            color={ff.ink}
            _placeholder={{ color: ff.muted }}
            maxLength={PROPOSAL_NOTE_LIMIT}
            placeholder="e.g. I've worked with their marketing lead on two campaigns — we talk regularly."
            value={values.proposalNote}
            onChange={e => onChange('proposalNote', e.target.value.slice(0, PROPOSAL_NOTE_LIMIT))}
          />
        </Box>
        {fieldHelp('proposalNote')}
      </Box>
    </Flex>
  );
};

export default ProposalFormFields;
